import React from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import Tooltip from "react-native-walkthrough-tooltip";
import Text from "@components/CustomText";
import {
  NotFound,
  WrongItem,
  DamageItem,
  CxRto,
} from "@assets/svg/MyDeliveries";
import { meta } from "@constants/cplp";
import Cross from "@assets/svg/Cross";
import ChevronRight from "@assets/svg/ChevronRight";
import color from "@constants/color";
import t from "@constants/localization";
import IssueTag from "@screens/MyDeliveries/components/IssueTag";

const PATH = "screens/CustomerOrderItems";

interface SingleIssueTooltipProps {
  markingConfig: any;
  currentState: {
    item_damaged?: number;
    not_found?: number;
    wrong_item_received?: number;
    ofd_db_lost_damage?: number;
    cx_rto_predelivery?: number;
  };
  onSubmit: (data: Record<string, number>) => void;
  visible: boolean;
  setVisible: (value: boolean) => void;
}

const SingleIssueTooltip: React.FC<SingleIssueTooltipProps> = ({
  markingConfig,
  currentState,
  onSubmit,
  visible,
  setVisible,
}) => {
  const totalDefectiveItem =
    (currentState?.item_damaged || 0) +
    (currentState?.not_found || 0) +
    (currentState?.wrong_item_received || 0) +
    (currentState?.cx_rto_predelivery || 0) +
    (currentState?.ofd_db_lost_damage || 0);

  const getIcon = (key: string) => {
    if (key === "wrong_item_received") {
      return <WrongItem />;
    }
    if (key === "item_damaged") {
      return <DamageItem />;
    }
    if (key === "not_found") {
      return <NotFound />;
    }
    if (key === "cx_rto_predelivery") {
      return <CxRto />;
    }
    return <NotFound />;
  };

  return (
    <Tooltip
      isVisible={visible}
      backgroundColor={color.black02}
      contentStyle={styles.tooltipContent}
      showChildInTooltip={false}
      content={
        <>
          <View
            style={[
              styles.contentHeader,
              { width: markingConfig?.length === 3 ? "100%" : 222 },
            ]}
          >
            <Text variant="body_text_2">{meta.issue_header}</Text>
            <TouchableOpacity testID="cut" onPress={() => setVisible(false)}>
              <Cross height={14} width={14} />
            </TouchableOpacity>
          </View>
          <View
            style={[
              styles.actionContainer,
              markingConfig?.length === 3 && {
                width: "100%",
                flexWrap: "nowrap",
              },
            ]}
          >
            {markingConfig.map((item: any) => {
              return item?.view_only ? (
                <View style={styles.centerAlign} key={item.key}>
                  <IssueTag
                    title={item?.title || ""}
                    value={currentState?.[item.key]}
                  />
                </View>
              ) : (
                <TouchableOpacity
                  testID={item.key}
                  onPress={() =>
                    onSubmit(
                      currentState?.[item.key] > 0
                        ? { [item.key]: 0 }
                        : { [item.key]: 1 },
                    )
                  }
                  style={{
                    alignItems: "center",
                    marginBottom: 10,
                    width: "35%",
                  }}
                  activeOpacity={0.7}
                >
                  <View style={styles.centerAlign}>
                    <View style={styles.elevation}>{getIcon(item.key)}</View>
                    <Text variant="caption_2" style={styles.actionText}>
                      {item?.title || ""}
                    </Text>
                  </View>
                  {currentState?.[item.key] ? (
                    <View style={styles.defectiveCount}>
                      <Text variant="caption_1" style={styles.whiteText}>
                        {totalDefectiveItem}
                      </Text>
                    </View>
                  ) : null}
                </TouchableOpacity>
              );
            })}
          </View>
        </>
      }
      placement="bottom"
      onClose={() => setVisible(false)}
    >
      <TouchableOpacity
        style={styles.issuebtn}
        onPress={() => {
          setVisible(true);
        }}
        activeOpacity={0.9}
      >
        {totalDefectiveItem > 0 ? (
          <Text variant="body_text_3" style={styles.issueText}>
            समस्या बदलें
          </Text>
        ) : (
          <Text variant="body_text_3" style={styles.issueText}>
            समस्या बताये
          </Text>
        )}
        <ChevronRight height={14} width={14} pathFill={color.black} />
      </TouchableOpacity>
    </Tooltip>
  );
};

const styles = StyleSheet.create({
  tooltipContent: {
    elevation: 4,
    padding: 8,
    borderRadius: 8,
  },
  contentHeader: {
    width: 222,
    backgroundColor: "rgba(240, 240, 240, 1)",
    flexDirection: "row",
    justifyContent: "space-between",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    alignItems: "center",
  },
  actionContainer: {
    marginBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 15,
    paddingHorizontal: 25,
    flexWrap: "wrap",
    width: 222,
  },
  actionText: {
    color: "rgba(0, 0, 0, 0.56)",
    marginTop: 16,
    textAlign: "center",
  },
  defectiveCount: {
    backgroundColor: "rgba(255, 46, 0, 1)",
    position: "absolute",
    borderRadius: 20,
    paddingHorizontal: 7,
    paddingTop: 3,
    paddingBottom: 2,
    elevation: 4,
    marginTop: -8,
    right: 4,
  },
  elevation: {
    backgroundColor: "white",
    borderRadius: 16,
    width: 32,
    height: 32,
    elevation: 4,
    justifyContent: "center",
    paddingTop: 3,
    alignItems: "center",
  },
  centerAlign: {
    alignItems: "center",
  },
  whiteText: {
    color: "white",
    lineHeight: 16,
  },
  issueText: {
    color: color.black,
    marginRight: 4,
  },
  issuebtn: {
    padding: 5,
    paddingHorizontal: 12,
    borderRadius: 18,
    flexDirection: "row",
    elevation: 2,
    backgroundColor: color.white,
    alignItems: "center",
  },
});

export default SingleIssueTooltip;
